// Text catalog for the Sikkerhedsledelse / HSE service page.
// Icons and images live in src/pages/HsePage.tsx and are aligned by index with
// the card arrays here.

import { type Locale } from "../config";
import { type PageMeta } from "../head";

type ValueProp = { title: string; text: string };
type Phase = { label: string; title: string; intro: string; items: string[] };

type HseStrings = {
  meta: PageMeta;
  hero: { eyebrow: string; title: string; body: string; ctaPrimary: string; ctaSecondary: string };
  intro: { eyebrow: string; heading: string; body: string };
  values: { eyebrow: string; heading: string; intro: string; props: ValueProp[] };
  phasesSection: { eyebrow: string; heading: string; intro: string };
  phases: Phase[];
  quote: { text: string; attrib: string };
  expert: { eyebrow: string; heading: string; lead: string; items: string[]; outro: string; cta: string };
  cta: { heading: string; body: string; call: string; write: string };
};

export const hseStrings: Record<Locale, HseStrings> = {
  da: {
    meta: {
      title: "Sikkerhedsledelse (HSE) i byggeprojekter - MI Support",
      description:
        "MI Support tilbyder sikkerhedsledelse (HSE) og sikkerhedskoordinatorer, der forebygger ulykker og sikrer et sundt arbejdsmiljø på byggepladsen.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK3989_1350x900.jpg",
    },
    hero: {
      eyebrow: "Vores ydelser",
      title: "Sikkerhedsledelse (HSE)",
      body: "Det vigtigste formål med sikkerhedsledelse (HSE) er at undgå ulykker og sygdom blandt medarbejdere. Med en sikkerhedskoordinator kan I forebygge ulykker eller hændelser ved særlige opgaver og minimere risici for skader og sygdom under normale forhold. HSE er en international betegnelse og står for Health, Safety og Environment.",
      ctaPrimary: "Tag en uforpligtende snak",
      ctaSecondary: "Se alle ydelser",
    },
    intro: {
      eyebrow: "Sundhed, sikkerhed og miljø",
      heading: "Få et sundt og sikkert byggeprojekt",
      body: "Uanset om I repræsenterer bygherre, rådgiver eller projekterende, har I et stort ansvar og vigtige forpligtelser, når det kommer til sikkerhed, miljø og arbejdsmiljø. Hos MI Support har vi erfarne sikkerhedsledere og -koordinatorer, der kan håndtere alle led i byggeprojektet både før, under og efter handover. De opgaver, vores HSE-konsulenter udfører, er alle nævnt i den danske lovgivning for arbejdsmiljø og krav og kan variere efter byggeriets størrelse og udformning.",
    },
    values: {
      eyebrow: "Hvad indebærer sikkerhedsledelse (HSE)?",
      heading: "HSE – Health, Safety & Environment",
      intro:
        "Sikkerhedsledelse (HSE) står for Health, Safety and Environment og handler for os om at sikre sundhed, sikkerhed og miljø på arbejdspladsen. Det omfatter en bred vifte af aktiviteter og politikker, der sigter mod at beskytte medarbejderne mod risici og farer på arbejdspladsen, at minimere risici for skader og sygdomme og at beskytte miljøet mod potentielle farer.",
      props: [
        {
          title: "Health, Helbred",
          text: "MI Supports sikkerhedskoordinatorer sigter mod at beskytte medarbejderne mod potentielle helbredsproblemer, der kan opstå under et byggeprojekt. Vores arbejde kan først og fremmest indebære forebyggelse af fysiske skader eller eksponering af farlige kemikalier. Derudover kan det indebære at fremme medarbejdernes mentale sundhed by ved at skabe en positiv arbejdspladskultur og tilbyde ressourcer og støtte i forhold til stresshåndtering og andre mentale helbredsproblemer.",
        },
        {
          title: "Safety, Sikkerhed",
          text: "MI Supports sikkerhedskoordinatorer påtager sig ansvaret for at beskytte medarbejderne mod risici for skader eller ulykker under byggeprojektet. Dette kan inkludere alt fra fysiske farer som fald, kollisioner eller eksplosioner til farer, der kan opstå på grund af manglende træning eller utilstrækkeligt beskyttelsesudstyr. Vores vigtigste opgave er at identificere potentielle farer og minimere risici ved at implementere passende sikkerhedsprocedurer og politikker.",
        },
        {
          title: "Environment, Miljø",
          text: "MI Supports sikkerhedskoordinatorer har stort fokus på at minimere byggeprojektets påvirkning af det lokale og globale miljø. Vores opgave indebærer også at identificere og minimere arbejdets udledning af farlige stoffer og emissioner samt at minimere mængden af affald og forurening, som genereres af byggeprojektets aktiviteter. Derudover fokuserer vi også på, at projektet lever op til de relevante miljøstandarder og -lovgivning.",
        },
      ],
    },
    phasesSection: {
      eyebrow: "Sikkerhedskoordinatorens rolle",
      heading: "HSE fra projektering til aflevering",
      intro:
        "Vores sikkerhedskoordinatorer tager ansvar for HSE i alle byggeriets faser med struktur, dokumentation og tæt opfølgning på pladsen.",
    },
    phases: [
      {
        label: "Fase 01",
        title: "HSE i forberedelses- og projekteringsfasen",
        intro:
          "Inden projektets start udarbejder sikkerhedskoordinatoren HSE-retningslinjer og procedurer for bygherren. I projekteringsfasen udarbejdes en plan for sikkerhed og sundhed (PSS), der har til formål at sikre, at alle, der arbejder på pladsen, har et godt arbejdsmiljø. En PSS indeholder blandt andet:",
        items: [
          "Byggepladsens organisationsplan",
          "Byggepladstegninger",
          "Tidsplaner",
          "Beskrivelse af eksisterende forhold og risici som følge af projektets udformning",
          "Beskrivelse af byggepladsens færdsels- og andre fællesområder, herunder også forhold om orden, ryddelighed, snerydning, grusning og affaldshåndtering",
          "Afgrænsning af områder med særlige risici samt angivelse af specifikke foranstaltninger om f.eks. særligt farligt arbejde",
          "Procedurer for kontrol med sikkerhedsforanstaltninger",
          "Beredskabs- og evakueringsplaner",
        ],
      },
      {
        label: "Fase 02",
        title: "HSE i udførelsesfasen",
        intro:
          "Sikkerhedskoordinatoren skal udover at ajourføre overholdelsen af PSS'en også foretage tilpasninger efter byggeriets fremgang. Det er sikkerhedskoordinatorens ansvar at have overblik og være opdateret med nye lovgivninger og standarder under gennemførelse af projektet. Under projektets gennemførelse tager sikkerhedskoordinatoren ansvar for:",
        items: [
          "De obligatoriske sikkerhedsmøder",
          "Daglige runderinger",
          "PSS'en",
          "Ulykkesfrekvens",
          "Risikovurdering",
          "Sikkerhedspræsentation",
          "Kommunikation med autoriteter (f.eks. Arbejdstilsynet)",
          "Mønsterarbejdspladsrunderinger",
          "Håndtering af varmt arbejde",
        ],
      },
    ],
    quote: {
      text: "For mig som sikkerhedskoordinator er det vigtigt at være til stede på byggepladsen og samarbejde med bygherre og entreprenører om en sikker arbejdsplads.",
      attrib: "Dan Steen Larsen — Sikkerhedskoordinator i MI Support",
    },
    expert: {
      eyebrow: "Sikkerhedskoordinatorens ansvar",
      heading: "Få en fast tilknyttet sikkerhedsekspert",
      lead: "HSE ledes af en sikkerhedskoordinator, der har det overordnede ansvar for:",
      items: [
        "at planlægge de forskellige arbejder, herunder at lave en HSE-tavle",
        "at skabe samarbejde mellem entreprenører",
        "at koordinere aktiviteter, der beskytter medarbejdere mod ulykker og sundhedsrisici",
        "at arbejdsgiverne anvender forebyggelsesprincipper på en sammenhængende måde",
        "at alle arbejdsgivere på pladsen anvender Planen for Sikkerhed og Sundhed.",
      ],
      outro:
        "Med en sikkerhedskoordinator fra MI Support får I tilknyttet en fast konsulent, der vil få dyb indsigt i virksomheden og byggepladsen. Vi har et stort bagland af fagspecialister, som der kan trækkes på i forbindelse med ekstraordinære opgaver eller situationer.",
      cta: "Kontakt os og hør hvad vi kan tilbyde",
    },
    cta: {
      heading: "Skal vi varetage jeres sikkerhedsledelse (HSE)?",
      body: "Vil I sikre et trygt og sundt arbejdsmiljø på jeres byggeplads med fuld kontrol over sikkerhed, koordinering og myndighedskrav? Vi tager gerne en uforpligtende snak om jeres projekt og behov.",
      call: "Ring +45 5555 1212",
      write: "Skriv til os",
    },
  },

  en: {
    meta: {
      title: "Safety management (HSE) in construction - MI Support",
      description:
        "MI Support offers safety management (HSE) and safety coordinators who prevent accidents and ensure a healthy working environment on the construction site.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK3989_1350x900.jpg",
    },
    hero: {
      eyebrow: "Our services",
      title: "Safety management (HSE)",
      body: "The most important purpose of safety management (HSE) is to avoid accidents and illness among employees. With a safety coordinator you can prevent accidents or incidents during special tasks and minimise the risk of injury and illness under normal conditions. HSE is an international term standing for Health, Safety and Environment.",
      ctaPrimary: "Have a no-obligation chat",
      ctaSecondary: "See all services",
    },
    intro: {
      eyebrow: "Health, safety and environment",
      heading: "Get a healthy and safe construction project",
      body: "Whether you represent the client, advisor or designer, you have a great responsibility and important obligations when it comes to safety, environment and the working environment. At MI Support we have experienced safety managers and coordinators who can handle every stage of the construction project before, during and after handover. The tasks our HSE consultants perform are all set out in Danish working environment legislation and requirements and can vary according to the size and design of the construction.",
    },
    values: {
      eyebrow: "What does safety management (HSE) involve?",
      heading: "HSE – Health, Safety & Environment",
      intro:
        "Safety management (HSE) stands for Health, Safety and Environment and, for us, is about ensuring health, safety and environment in the workplace. It encompasses a broad range of activities and policies aimed at protecting employees from risks and dangers in the workplace, minimising the risk of injury and illness, and protecting the environment from potential hazards.",
      props: [
        {
          title: "Health",
          text: "MI Support's safety coordinators aim to protect employees from potential health problems that may arise during a construction project. Our work can first and foremost involve preventing physical injuries or exposure to hazardous chemicals. In addition, it can involve promoting employees' mental health by creating a positive workplace culture and offering resources and support related to stress management and other mental health issues.",
        },
        {
          title: "Safety",
          text: "MI Support's safety coordinators take on the responsibility of protecting employees from the risk of injury or accidents during the construction project. This can include everything from physical hazards such as falls, collisions or explosions to dangers that may arise due to a lack of training or inadequate protective equipment. Our most important task is to identify potential hazards and minimise risks by implementing appropriate safety procedures and policies.",
        },
        {
          title: "Environment",
          text: "MI Support's safety coordinators place great emphasis on minimising the construction project's impact on the local and global environment. Our task also involves identifying and minimising the work's emissions of hazardous substances, as well as minimising the amount of waste and pollution generated by the construction project's activities. In addition, we also focus on the project meeting the relevant environmental standards and legislation.",
        },
      ],
    },
    phasesSection: {
      eyebrow: "The safety coordinator's role",
      heading: "HSE from design to handover",
      intro:
        "Our safety coordinators take responsibility for HSE in all phases of the construction with structure, documentation and close follow-up on site.",
    },
    phases: [
      {
        label: "Phase 01",
        title: "HSE in the preparation and design phase",
        intro:
          "Before the start of the project, the safety coordinator prepares HSE guidelines and procedures for the client. In the design phase, a health and safety plan (PSS) is drawn up to ensure that everyone working on the site has a good working environment. A PSS includes, among other things:",
        items: [
          "The construction site's organisation plan",
          "Construction site drawings",
          "Schedules",
          "Description of existing conditions and risks resulting from the project's design",
          "Description of the construction site's access routes and other common areas, including matters of order, tidiness, snow clearing, gritting and waste handling",
          "Demarcation of areas with particular risks and specification of specific measures for, for example, particularly hazardous work",
          "Procedures for controlling safety measures",
          "Emergency and evacuation plans",
        ],
      },
      {
        label: "Phase 02",
        title: "HSE in the execution phase",
        intro:
          "In addition to keeping compliance with the PSS up to date, the safety coordinator must also make adjustments as the construction progresses. It is the safety coordinator's responsibility to maintain an overview and stay up to date with new legislation and standards during the implementation of the project. During the implementation of the project, the safety coordinator takes responsibility for:",
        items: [
          "The mandatory safety meetings",
          "Daily walkarounds",
          "The PSS",
          "Accident frequency",
          "Risk assessment",
          "Safety presentation",
          "Communication with authorities (e.g. the Danish Working Environment Authority)",
          "Model workplace walkarounds",
          "Management of hot work",
        ],
      },
    ],
    quote: {
      text: "For me as a safety coordinator it is important to be present on the construction site and to work together with the client and contractors on a safe workplace.",
      attrib: "Dan Steen Larsen — Safety coordinator at MI Support",
    },
    expert: {
      eyebrow: "The safety coordinator's responsibility",
      heading: "Get a dedicated safety expert",
      lead: "HSE is led by a safety coordinator who has overall responsibility for:",
      items: [
        "planning the various works, including creating an HSE board",
        "establishing collaboration between contractors",
        "coordinating activities that protect employees from accidents and health risks",
        "ensuring that employers apply prevention principles in a coherent manner",
        "ensuring that all employers on the site use the Health and Safety Plan.",
      ],
      outro:
        "With a safety coordinator from MI Support you get a dedicated consultant who will gain deep insight into the company and the construction site. We have a large network of specialists who can be drawn upon in connection with extraordinary tasks or situations.",
      cta: "Contact us and hear what we can offer",
    },
    cta: {
      heading: "Should we handle your safety management (HSE)?",
      body: "Do you want to ensure a safe and healthy working environment on your construction site with full control over safety, coordination and regulatory requirements? We're happy to have a no-obligation chat about your project and needs.",
      call: "Call +45 5555 1212",
      write: "Write to us",
    },
  },

  de: {
    meta: {
      title: "Sicherheitsmanagement (HSE) am Bau - MI Support",
      description:
        "MI Support bietet Sicherheitsmanagement (HSE) und Sicherheitskoordinatoren, die Unfälle verhindern und ein gesundes Arbeitsumfeld auf der Baustelle sichern.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK3989_1350x900.jpg",
    },
    hero: {
      eyebrow: "Unsere Leistungen",
      title: "Sicherheitsmanagement (HSE)",
      body: "Der wichtigste Zweck des Sicherheitsmanagements (HSE) ist es, Unfälle und Erkrankungen bei den Mitarbeitern zu vermeiden. Mit einem Sicherheitskoordinator können Sie Unfälle oder Vorfälle bei besonderen Aufgaben verhindern und das Risiko von Verletzungen und Erkrankungen unter normalen Bedingungen minimieren. HSE ist eine internationale Bezeichnung und steht für Health, Safety und Environment.",
      ctaPrimary: "Unverbindliches Gespräch führen",
      ctaSecondary: "Alle Leistungen ansehen",
    },
    intro: {
      eyebrow: "Gesundheit, Sicherheit und Umwelt",
      heading: "Erhalten Sie ein gesundes und sicheres Bauprojekt",
      body: "Ob Sie nun den Bauherrn, den Berater oder den Planer vertreten – Sie tragen eine große Verantwortung und wichtige Pflichten, wenn es um Sicherheit, Umwelt und Arbeitsumfeld geht. Bei MI Support haben wir erfahrene Sicherheitsleiter und -koordinatoren, die jede Phase des Bauprojekts vor, während und nach der Übergabe bewältigen können. Die Aufgaben, die unsere HSE-Berater ausführen, sind alle in der dänischen Gesetzgebung zum Arbeitsumfeld und in den Anforderungen genannt und können je nach Größe und Gestaltung des Baus variieren.",
    },
    values: {
      eyebrow: "Was umfasst Sicherheitsmanagement (HSE)?",
      heading: "HSE – Health, Safety & Environment",
      intro:
        "Sicherheitsmanagement (HSE) steht für Health, Safety and Environment und bedeutet für uns, Gesundheit, Sicherheit und Umwelt am Arbeitsplatz zu gewährleisten. Es umfasst eine breite Palette von Aktivitäten und Richtlinien, die darauf abzielen, die Mitarbeiter vor Risiken und Gefahren am Arbeitsplatz zu schützen, das Risiko von Verletzungen und Erkrankungen zu minimieren und die Umwelt vor potenziellen Gefahren zu schützen.",
      props: [
        {
          title: "Health, Gesundheit",
          text: "Die Sicherheitskoordinatoren von MI Support zielen darauf ab, die Mitarbeiter vor potenziellen Gesundheitsproblemen zu schützen, die während eines Bauprojekts auftreten können. Unsere Arbeit kann in erster Linie die Vorbeugung von körperlichen Verletzungen oder die Exposition gegenüber gefährlichen Chemikalien umfassen. Darüber hinaus kann sie die Förderung der psychischen Gesundheit der Mitarbeiter umfassen, indem eine positive Arbeitsplatzkultur geschaffen und Ressourcen und Unterstützung im Hinblick auf Stressbewältigung und andere psychische Gesundheitsprobleme angeboten werden.",
        },
        {
          title: "Safety, Sicherheit",
          text: "Die Sicherheitskoordinatoren von MI Support übernehmen die Verantwortung, die Mitarbeiter vor dem Risiko von Verletzungen oder Unfällen während des Bauprojekts zu schützen. Dies kann alles umfassen, von physischen Gefahren wie Stürzen, Kollisionen oder Explosionen bis hin zu Gefahren, die aufgrund mangelnder Schulung oder unzureichender Schutzausrüstung entstehen können. Unsere wichtigste Aufgabe ist es, potenzielle Gefahren zu identifizieren und Risiken zu minimieren, indem geeignete Sicherheitsverfahren und -richtlinien umgesetzt werden.",
        },
        {
          title: "Environment, Umwelt",
          text: "Die Sicherheitskoordinatoren von MI Support legen großen Wert darauf, die Auswirkungen des Bauprojekts auf die lokale und globale Umwelt zu minimieren. Unsere Aufgabe umfasst auch, die Emissionen gefährlicher Stoffe und Emissionen der Arbeit zu identifizieren und zu minimieren sowie die Menge an Abfall und Verschmutzung zu minimieren, die durch die Aktivitäten des Bauprojekts entsteht. Darüber hinaus achten wir auch darauf, dass das Projekt die relevanten Umweltstandards und -gesetze erfüllt.",
        },
      ],
    },
    phasesSection: {
      eyebrow: "Die Rolle des Sicherheitskoordinators",
      heading: "HSE von der Planung bis zur Übergabe",
      intro:
        "Unsere Sicherheitskoordinatoren übernehmen die Verantwortung für HSE in allen Phasen des Baus – mit Struktur, Dokumentation und enger Nachverfolgung auf der Baustelle.",
    },
    phases: [
      {
        label: "Phase 01",
        title: "HSE in der Vorbereitungs- und Planungsphase",
        intro:
          "Vor Projektbeginn erstellt der Sicherheitskoordinator HSE-Richtlinien und -Verfahren für den Bauherrn. In der Planungsphase wird ein Plan für Sicherheit und Gesundheit (PSS) erstellt, der sicherstellen soll, dass alle, die auf der Baustelle arbeiten, ein gutes Arbeitsumfeld haben. Ein PSS enthält unter anderem:",
        items: [
          "Den Organisationsplan der Baustelle",
          "Baustellenzeichnungen",
          "Zeitpläne",
          "Beschreibung der bestehenden Verhältnisse und Risiken infolge der Gestaltung des Projekts",
          "Beschreibung der Verkehrs- und sonstigen Gemeinschaftsflächen der Baustelle, einschließlich Fragen zu Ordnung, Sauberkeit, Schneeräumung, Streuung und Abfallentsorgung",
          "Abgrenzung von Bereichen mit besonderen Risiken sowie Angabe spezifischer Maßnahmen z. B. für besonders gefährliche Arbeiten",
          "Verfahren zur Kontrolle von Sicherheitsmaßnahmen",
          "Notfall- und Evakuierungspläne",
        ],
      },
      {
        label: "Phase 02",
        title: "HSE in der Ausführungsphase",
        intro:
          "Der Sicherheitskoordinator muss neben der Aktualisierung der Einhaltung des PSS auch Anpassungen entsprechend dem Baufortschritt vornehmen. Es liegt in der Verantwortung des Sicherheitskoordinators, den Überblick zu behalten und während der Durchführung des Projekts über neue Gesetze und Normen auf dem Laufenden zu bleiben. Während der Durchführung des Projekts übernimmt der Sicherheitskoordinator die Verantwortung für:",
        items: [
          "Die obligatorischen Sicherheitsbesprechungen",
          "Tägliche Rundgänge",
          "Das PSS",
          "Unfallhäufigkeit",
          "Risikobewertung",
          "Sicherheitspräsentation",
          "Kommunikation mit Behörden (z. B. der dänischen Arbeitsaufsicht)",
          "Musterarbeitsplatz-Rundgänge",
          "Handhabung von Heißarbeiten",
        ],
      },
    ],
    quote: {
      text: "Für mich als Sicherheitskoordinator ist es wichtig, auf der Baustelle präsent zu sein und mit dem Bauherrn und den Auftragnehmern an einem sicheren Arbeitsplatz zu arbeiten.",
      attrib: "Dan Steen Larsen — Sicherheitskoordinator bei MI Support",
    },
    expert: {
      eyebrow: "Die Verantwortung des Sicherheitskoordinators",
      heading: "Erhalten Sie einen fest zugeordneten Sicherheitsexperten",
      lead: "HSE wird von einem Sicherheitskoordinator geleitet, der die Gesamtverantwortung trägt für:",
      items: [
        "die Planung der verschiedenen Arbeiten, einschließlich der Erstellung einer HSE-Tafel",
        "die Schaffung der Zusammenarbeit zwischen den Auftragnehmern",
        "die Koordination von Aktivitäten, die die Mitarbeiter vor Unfällen und Gesundheitsrisiken schützen",
        "dass die Arbeitgeber die Präventionsgrundsätze auf zusammenhängende Weise anwenden",
        "dass alle Arbeitgeber auf der Baustelle den Plan für Sicherheit und Gesundheit anwenden.",
      ],
      outro:
        "Mit einem Sicherheitskoordinator von MI Support erhalten Sie einen fest zugeordneten Berater, der tiefe Einblicke in das Unternehmen und die Baustelle gewinnt. Wir verfügen über ein großes Netzwerk von Fachspezialisten, auf das bei außergewöhnlichen Aufgaben oder Situationen zurückgegriffen werden kann.",
      cta: "Kontaktieren Sie uns und erfahren Sie, was wir anbieten können",
    },
    cta: {
      heading: "Sollen wir Ihr Sicherheitsmanagement (HSE) übernehmen?",
      body: "Möchten Sie ein sicheres und gesundes Arbeitsumfeld auf Ihrer Baustelle mit voller Kontrolle über Sicherheit, Koordination und behördliche Anforderungen sicherstellen? Wir führen gern ein unverbindliches Gespräch über Ihr Projekt und Ihre Bedürfnisse.",
      call: "Anrufen +45 5555 1212",
      write: "Schreiben Sie uns",
    },
  },
};
